/**
 * AdminHeader Component - Microsoft 365 Admin Console Style
 * Teal header bar with search, notifications, and user profile
 */

import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  SearchBox,
  Avatar,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
} from '@fluentui/react-components';
import {
  SearchRegular,
  AlertRegular,
  PersonRegular,
  SignOutRegular,
  SettingsRegular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../contexts/AppContainer';
import { ApiClient, VerifyApiClient, normalizeImageUrl } from '../../configurations/index.config';
import { VERIFY_API_URL } from '../../configurations/credentials';
import defaultImage from './../../images/profile_avatar.png';
import defaultLogo from './../../images/logo.png';

const useStyles = makeStyles({
  header: {
    backgroundColor: '#008272', // M365 admin teal
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 20px',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    cursor: 'pointer',
    ':hover': {
      opacity: 0.9,
    },
  },
  logo: {
    height: '24px',
    width: 'auto',
  },
  appTitle: {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily:
      "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
    marginLeft: '12px',
  },
  searchContainer: {
    flex: 1,
    maxWidth: '600px',
    margin: '0 24px',
  },
  searchBox: {
    width: '100%',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  iconButton: {
    color: '#ffffff',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  avatar: {
    cursor: 'pointer',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    ':hover': {
      border: '2px solid rgba(255, 255, 255, 0.6)',
    },
  },
  userMenuContent: {
    padding: '8px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  userName: {
    fontSize: '14px',
    fontWeight: '600',
    fontFamily:
      "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
  },
  userEmail: {
    fontSize: '12px',
    fontFamily:
      "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
    color: '#605e5c',
  },
});

export const AdminHeader: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { userData, profileData, clearUser } = useAppContext() as any; // Context is JS, need type assertion
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');

  const fetchProfileImage = async (id: string) => {
    if (!id) {
      return;
    }
    try {
      const response = await VerifyApiClient.get(`/profile/get?employee_id=${id}`);
      if (response.data.status === 'success') {
        setImageUrl(response.data.image);
      }
    } catch (error) {
      console.error('Error fetching image:', error);
    }
  };

  useEffect(() => {
    if (profileData?.employeeId) {
      fetchProfileImage(profileData.employeeId);
    }
  }, [profileData?.employeeId]);

  useEffect(() => {
    const fetchCompanyLogo = async () => {
      try {
        const response = await ApiClient.get('/api/v1/company-setup');
        if (response.data.company?.logo_url) {
          setCompanyLogo(normalizeImageUrl(response.data.company.logo_url) || null);
        }
      } catch (error) {
        // Silently fail, will use default logo
      }
    };
    fetchCompanyLogo();
  }, []);

  const handleLogout = () => {
    clearUser();
    navigate('/login');
  };

  const handleProfileClick = () => {
    navigate('/account/profile');
  };

  const handleSettingsClick = () => {
    navigate('/account/settings');
  };

  const handleLogoClick = () => {
    navigate('/account');
  };

  const userDisplayName = profileData?.employee
    ? `${profileData.employee.first_name || ''} ${profileData.employee.last_name || ''}`.trim()
    : 'Admin User';

  const avatarImageUrl = imageUrl ? `${VERIFY_API_URL}${imageUrl}` : defaultImage;

  return (
    <header className={styles.header}>
      {/* Left Section - Logo & Title */}
      <div className={styles.leftSection} onClick={handleLogoClick} title="Go to Dashboard">
        <img src={companyLogo || defaultLogo} alt="Logo" className={styles.logo} />
        <Text className={styles.appTitle}>Security Admin Center</Text>
      </div>

      {/* Center Section - Search */}
      <div className={styles.searchContainer}>
        <SearchBox
          className={styles.searchBox}
          placeholder="Search employees, clients, sites..."
          value={searchValue}
          onChange={(_, data) => setSearchValue(data?.value || '')}
          contentBefore={<SearchRegular />}
          appearance="filled-lighter"
          size="medium"
        />
      </div>

      {/* Right Section - Actions & User */}
      <div className={styles.rightSection}>
        {/* Notifications (placeholder) */}
        <Button
          icon={<AlertRegular />}
          appearance="subtle"
          className={styles.iconButton}
          title="Notifications"
        />

        {/* User Menu */}
        <Menu>
          <MenuTrigger>
            <Avatar
              name={userDisplayName}
              image={{ src: avatarImageUrl }}
              size={28}
              className={styles.avatar}
            />
          </MenuTrigger>

          <MenuPopover>
            <MenuList>
              {/* User Info */}
              <div className={styles.userMenuContent}>
                <Text className={styles.userName}>{userDisplayName}</Text>
                <Text className={styles.userEmail}>
                  {profileData?.employee?.email || userData?.username || 'admin@security.com'}
                </Text>
              </div>

              <MenuDivider />

              {/* Menu Items */}
              <MenuItem icon={<PersonRegular />} onClick={handleProfileClick}>
                My Profile
              </MenuItem>
              <MenuItem icon={<SettingsRegular />} onClick={handleSettingsClick}>
                Settings
              </MenuItem>

              <MenuDivider />

              <MenuItem icon={<SignOutRegular />} onClick={handleLogout}>
                Sign Out
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
    </header>
  );
};
